import { UploadedFileEntry } from '@interfaces/mfe/mfe-document-upload.interface';
import {
  AccountTypes,
  IRADesignationListedTypes,
  IRAOptions,
  IRATrustTypesMapper,
  IReceipt,
  ReceiptRow,
} from '@interfaces/models';
import { VgEmploymentState } from '@interfaces/models/open-account-mfes/employment-mfe.model';
import {
  PersonalAddressData,
  PersonalAddressGetData,
} from '@interfaces/models/open-account-mfes/personal-address-mfe.model';
import { JOINT_ACCOUNTS_TYPES } from '@interfaces/state';
import { createSelector } from '@ngrx/store';
import { ClearData, RelationshipDesignationTypes } from '@shared/models';
import { Routes } from '@shared/models/routes.enum';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { getFormRelationshipType } from '@src/app/secure/account-triage/helpers';
import { TriageRelationshipTypeNames } from '@src/app/secure/account-triage/models/triage-relationship.model';
import * as fromFeature from '@store/reducers';
import { TransactionState } from '@store/reducers';
import { getMfePersonalAddressState } from '@store/reducers';
import { BeneficiariesState } from '@store/reducers/beneficiaries/beneficiaries.reducer';
import { FinancialDetailsState } from '@store/reducers/income-net-worth/income-net-worth.reducer';
import { ILetterUploadState } from '@store/reducers/letter-upload/letter-upload.reducer';
import { MfePersonalInfoClientState } from '@store/reducers/mfe-personal-info-client/mfe-personal-info-client.reducer';
import { MfePersonalAddressState } from '@store/reducers/personal-info-address/personal-info-address.reducers';
import { MfePhoneState } from '@store/reducers/personal-phone/personal-phone.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import {
  selectIncomeNetWorth,
  selectInheritanceDecedentSecurityToggle,
  selectInheritanceIndividualJointToTrustToggle,
  selectInheritanceVCDToggle,
  selectMultiInheritanceEstateToggle,
  selectTriageState,
} from '@store/selectors';
import {
  selectClearData,
  selectWldLetterDocuments,
} from '@store/selectors/document-upload/document-upload.selectors';
import { selectInheritanceNewTermsToggle } from '@store/selectors/feature/feature.selectors';
import { selectLetterUploadState } from '@store/selectors/letter-upload/letter-upload.selectors';
import { selectMfePersonalInfoClientPersonalInfo } from '@store/selectors/mfe-personal-info-client/mfe-personal-info-client.selectors';
import { selectMfePersonalInfoClientSSN } from '@store/selectors/mfe-personal-info-client/mfe-personal-info-client.selectors';
import { selectEmploymentMfeDataByOwnerIndex } from '@store/selectors/open-account/open-account.selectors';
import { selectMfePhone } from '@store/selectors/personal-phone/personal-phone.selectors';
import {
  buildAccountToMultiInheritance,
  getMultiInheritanceAccountFromType,
} from '@store/selectors/receipt/cashier-nonretirement';
import {
  selectBeneByRelationshipReceipt,
  selectDecedentInfoReceipt,
  selectDocumentUploadReceipt,
  selectFundingReceipt,
} from '@store/selectors/receipt/inheritance/inheritance-receipt.selectors';
import {
  buildDefaultBeneficiaryMultiInheritanceReceipt,
  buildEmploymentMultiInheritanceReceipt,
  buildEstateMultiInheritanceReceipt,
  buildFinancialProfileMultiInheritanceReceipt,
  buildMultiInheritanceProfileReceipt,
  buildTrustInfoMultiInheritanceReceipt,
} from '@store/selectors/receipt/multi-inheritance/multi-inheritance-receipt-builders';
import {
  selectAllTransactions,
  selectAllTrustTransactions,
  selectIsNewAccountTransactionExist,
  selectTransactionsTotal,
} from '@store/selectors/transaction-state/transaction-state.selectors';
import { selectClientPersonalInfoData, selectIsPersonalInfoSSN } from 'vg-interaction-model';

import { buildBeneByRelationshipReceipt } from '../inheritance/cashier-ira';

export const selectIsIraMultiInheritanceFlow = createSelector(
  selectAllTransactions,
  (state: fromFeature.TransactionState[]) => {
    return state.some(
      (transaction: TransactionState) =>
        transaction.accountTriage.accountTypeInheriting === AccountTypes.IRA,
    );
  },
);

export function buildTransferReceipt(
  transaction: TransactionState,
  index: number,
  count: number,
): IReceipt {
  const { accountTriage, selectedClientAccounts, currentTransactionId } = transaction;
  const { accountTypeInheriting, isSpouse, IRADesignation, individualDesignation } = accountTriage;
  const transactionReceipt: IReceipt = {
    header: `Transfer ${index + 1}`,
    description: null,
    editable: true,
    removable: count > 1,
    editRoute: 'secure/multi-inheritance/account-triage',
    sectionEvidenceID: 'transaction',
    editTransferId: currentTransactionId,
    rows: [],
  };

  transactionReceipt.rows = [
    {
      label: 'Account type',
      values: [getMultiInheritanceAccountFromType(transaction)],
      evidenceID: 'toAccountType',
    },
  ];

  if (JOINT_ACCOUNTS_TYPES.includes(accountTriage.accountTypeInheriting)) {
    transactionReceipt.rows.push({
      label: 'Account number',
      values:
        selectedClientAccounts.transferFrom?.selectedAccounts?.map((acc) => acc.accountNumber) ||
        [],
      evidenceID: 'toAccountNumber0',
    });
  }

  transactionReceipt.rows.push({
    label: 'How were you named or listed on the account?',
    values: [
      TriageRelationshipTypeNames[
        getFormRelationshipType(
          accountTypeInheriting,
          isSpouse,
          IRADesignation,
          individualDesignation,
        ) as keyof typeof TriageRelationshipTypeNames
      ],
    ],
    evidenceID: 'relationship',
  });

  if (accountTriage.wasAccountPreviouslyInherited) {
    transactionReceipt.rows.push({
      label: 'Was the account previously inherited by the decedent?',
      values: [accountTriage.wasAccountPreviouslyInherited],
      evidenceID: 'accountPreviouslyInherited',
    });
  }

  if (accountTriage.originalDecedentName) {
    transactionReceipt.rows.push({
      label: 'What was the original decedents name?',
      values: [accountTriage.originalDecedentName],
      evidenceID: 'originalDecedentName',
    });
  }

  if (accountTriage.isOnlyExecutor) {
    transactionReceipt.rows.push({
      label: 'Are you the only executor?',
      values: [accountTriage.isOnlyExecutor],
      evidenceID: 'isOnlyExecutor',
    });
  }

  if (accountTriage.isOnlyTrustee) {
    transactionReceipt.rows.push({
      label: 'Are you the only trustee?',
      values: [accountTriage.isOnlyTrustee],
      evidenceID: 'isOnlyTrustee',
    });
  }

  if (accountTriage.isOnlyBeneficiary) {
    transactionReceipt.rows.push({
      label: 'Are you the only listed beneficiary?',
      values: [accountTriage.isOnlyBeneficiary],
      evidenceID: 'isOnlyBeneficiary',
    });
  }

  if (accountTriage.typeOfTrust) {
    transactionReceipt.rows.push({
      label: 'What type of account are you inheriting into?',
      values: [IRATrustTypesMapper[accountTriage.typeOfTrust]],
      evidenceID: 'trustType',
    });
  }

  if (selectedClientAccounts.transferTo?.iraOption) {
    transactionReceipt.rows.push({
      label: 'Do you want to assume or inherit the account?',
      values: [
        IRAOptions.ASSUME === selectedClientAccounts.transferTo.iraOption ? 'Assume' : 'Inherit',
      ],
      evidenceID: 'assumeOrInherit',
    });
  }

  if (accountTriage.wasMarried) {
    transactionReceipt.rows.push({
      label: 'Was the decedent married at the time of death?',
      values: [accountTriage.wasMarried],
      evidenceID: 'decedentMarried',
    });
  }

  if (accountTriage.isDecedentSpouse) {
    transactionReceipt.rows.push({
      label: 'Are you the decedents spouse?',
      values: [accountTriage.isDecedentSpouse],
      evidenceID: 'isDecedentSpouse',
    });
  }

  transactionReceipt.rows.push({
    label: 'Where would you like to transfer the investments?',
    values: [
      selectedClientAccounts.transactionType === 'EXISTING'
        ? selectedClientAccounts.transferTo?.selectedAccount.accountData.accountName
        : `Open a new ${
            JOINT_ACCOUNTS_TYPES.includes(accountTriage.accountTypeInheriting) ||
            accountTriage.accountTypeInheriting === AccountTypes.INDV
              ? 'Individual'
              : accountTriage.accountTypeInheriting
          }`,
    ],
    evidenceID: 'toAccountRegistration',
  });

  return transactionReceipt;
}

export function buildMultiBeneReceipt(
  currentTransaction: TransactionState,
  personalInfo: MfePersonalInfoClientState['personalInfo'],
) {
  const beneficiaries: BeneficiariesState = {
    onlyChild: currentTransaction.accountTriage.isOnlyChild || false,
    otherBenes: currentTransaction.beneficiaries,
    relationshipDesignation:
      currentTransaction.accountTriage.IRADesignation === IRADesignationListedTypes.RELATIONSHIP &&
      currentTransaction.accountTriage.isSpouse === 'NO'
        ? RelationshipDesignationTypes.DESCENDANT
        : undefined,
  };
  return buildBeneByRelationshipReceipt(
    currentTransaction,
    beneficiaries,
    currentTransaction.accountTriage,
    personalInfo,
    true,
    true,
  );
}

export function buildTransfersAndAccountsReceipt(
  transactions: TransactionState[] | null,
  triageState: TriageState,
  personalInfo: MfePersonalInfoClientState['personalInfo'],
  ssn_private: string,
  address: MfePersonalAddressState,
  phone: MfePhoneState,
  clearData: ClearData,
  VCDToggleOn: boolean,
  multiEstateToggleOn: boolean,
  individualJointToTrustToggleOn: boolean,
  editable: boolean = true,
  hideWithholding: boolean = false,
): IReceipt[] {
  const receipts: IReceipt[] = [];

  if (!transactions) {
    return receipts;
  }

  transactions.forEach(
    (currentTransaction: TransactionState, index: number, array: TransactionState[]) => {
      const transferReceipt = buildTransferReceipt(currentTransaction, index, array.length);
      receipts.push(transferReceipt);

      const beneReceipt = buildMultiBeneReceipt(currentTransaction, personalInfo);

      if (beneReceipt) {
        receipts.push(beneReceipt);
      }

      const isNewAccountTransaction =
        currentTransaction.selectedClientAccounts.transactionType !== 'EXISTING';

      let accountReceipt =
        isNewAccountTransaction &&
        buildAccountToMultiInheritance(
          currentTransaction,
          VCDToggleOn,
          multiEstateToggleOn,
          individualJointToTrustToggleOn,
          undefined,
          undefined,
          false,
        );

      if (accountReceipt) {
        accountReceipt = {
          ...accountReceipt,
          header: 'New account',
          rows: accountReceipt.rows.map((row: ReceiptRow) => {
            if (row.evidenceID === 'investmentObjectives') {
              return {
                ...row,
                banner: {
                  title: 'Important info about your new account',
                  text: "Once your new account is opened, we'll send a confirmation with all the account details to your message center.",
                },
              };
            }
            return row;
          }),
        };
        receipts.push(accountReceipt);
      }

      const estateReceipt = buildEstateMultiInheritanceReceipt(
        currentTransaction,
        editable,
        undefined,
        hideWithholding,
      );

      if (estateReceipt) {
        receipts.push(estateReceipt);
      }

      // only for ira to trust flow
      const trustInfoReceipt = buildTrustInfoMultiInheritanceReceipt(
        currentTransaction,
        editable,
        hideWithholding,
      );

      if (trustInfoReceipt) {
        receipts.push(trustInfoReceipt);
      }

      const profile = buildMultiInheritanceProfileReceipt(
        currentTransaction,
        triageState,
        personalInfo,
        ssn_private,
        address,
        phone,
        clearData,
        isNewAccountTransaction,
        editable,
      );

      receipts.push(profile);
    },
  );
  return receipts;
}

export const getStandardMultiInheritanceReceiptData = (editable: boolean) =>
  createSelector(
    selectDecedentInfoReceipt(editable),
    selectDocumentUploadReceipt(),
    selectBeneByRelationshipReceipt(),
    selectAllTransactions,
    selectTriageState,
    selectMfePersonalInfoClientPersonalInfo,
    selectMfePersonalInfoClientSSN,
    getMfePersonalAddressState,
    selectMfePhone,
    selectClearData,
    selectFundingReceipt(false),
    selectInheritanceVCDToggle,
    selectMultiInheritanceEstateToggle,
    selectInheritanceIndividualJointToTrustToggle,
    selectInheritanceDecedentSecurityToggle,
    selectInheritanceNewTermsToggle,
    (
      owner,
      documents,
      beneByRelationship,
      transactions,
      triageState: TriageState,
      personalInfo: MfePersonalInfoClientState['personalInfo'],
      ssn_private: string,
      address: MfePersonalAddressState,
      phone: MfePhoneState,
      clearData: ClearData,
      funding,
      VCDToggleOn,
      multiEstateToggleOn,
      individualJointToTrustToggleOn,
      decedentSecurityToggleOn,
      hideWithholding: boolean,
    ) => {
      const documentsReceipt = documents
        ? { ...documents, editRoute: 'secure/multi-inheritance/upload' }
        : null;

      const transfersAndAccounts = buildTransfersAndAccountsReceipt(
        transactions || null,
        triageState,
        personalInfo,
        ssn_private,
        address,
        phone,
        clearData,
        VCDToggleOn,
        multiEstateToggleOn,
        individualJointToTrustToggleOn,
        editable,
        hideWithholding,
      );

      const ownerEditRoute: string = decedentSecurityToggleOn
        ? Routes.MULTI_INHERITANCE_DECEDENT_INFORMATION
        : Routes.MULTI_INHERITANCE_DECEDENT_INFO;

      return {
        owner: { ...owner, editRoute: ownerEditRoute },
        documents: documentsReceipt,
        beneByRelationship,
        transfersAndAccounts,
        funding,
      };
    },
  );

export const selectEmploymentMultiInheritanceReceipt = (
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.employment,
) =>
  createSelector(
    selectEmploymentMfeDataByOwnerIndex(0),
    selectIsNewAccountTransactionExist,
    selectTriageState,
    selectLetterUploadState,
    selectWldLetterDocuments,
    (
      state: Partial<VgEmploymentState> | undefined,
      isNewAccountTransactionExist: boolean,
      triageState: TriageState,
      letterUploadState: ILetterUploadState,
      wldLetterDocuments: UploadedFileEntry[],
    ) =>
      buildEmploymentMultiInheritanceReceipt(
        state,
        isNewAccountTransactionExist,
        triageState,
        letterUploadState,
        editable,
        content,
        wldLetterDocuments,
      ),
  );

export const selectFinancialProfileMultiInheritanceReceipt = (
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.financialProfile,
) =>
  createSelector(
    selectIncomeNetWorth,
    selectIsNewAccountTransactionExist,
    selectAllTrustTransactions,
    selectTransactionsTotal,
    (
      financial: FinancialDetailsState,
      isNewAccountTransactionExist: boolean,
      trustTransactions: TransactionState[],
      transactionsCount: number,
    ) => {
      return buildFinancialProfileMultiInheritanceReceipt(
        financial,
        isNewAccountTransactionExist,
        editable,
        content,
      );
    },
  );

export const selectDefaultBeneficiaryMultiInheritanceReceipt = () =>
  createSelector(
    selectIsNewAccountTransactionExist,
    selectIsIraMultiInheritanceFlow,
    selectAllTrustTransactions,
    selectTransactionsTotal,
    (
      isNewAccountTransactionExist: boolean,
      isIraMultiInheritanceFlow: boolean,
      trustTransactions: TransactionState[],
      transactionsCount: number,
    ) =>
      buildDefaultBeneficiaryMultiInheritanceReceipt(
        isNewAccountTransactionExist,
        isIraMultiInheritanceFlow,
        trustTransactions,
        transactionsCount,
      ),
  );

export const getNewAccountMultiInheritanceReceiptData = createSelector(
  selectEmploymentMultiInheritanceReceipt(),
  selectFinancialProfileMultiInheritanceReceipt(),
  selectDefaultBeneficiaryMultiInheritanceReceipt(),
  (employment, financial, defaultBeneficiary) => {
    return {
      employment,
      financial,
      defaultBeneficiary,
    };
  },
);
export const selectMultiInheritanceReceiptData = (editable: boolean = true) =>
  createSelector(
    getStandardMultiInheritanceReceiptData(editable),
    getNewAccountMultiInheritanceReceiptData,
    (standardReceiptData, newAccountReceiptData) => {
      return {
        ...standardReceiptData,
        ...newAccountReceiptData,
      };
    },
  );
